import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import { useLocation } from 'react-router'
import classnames from 'classnames'

import UnauthorizedPage from '../index'
import Form from '../../../components/Form'
import Field from '../../../components/Form/Field'
import InternalLink from '../../../components/InternalLink'
import FieldLabelWrapper from '../../../features/components/Form/FieldLabelWrapper'
import { getAppDomainName } from '../../../features/helpers/getAppDomainName'

import { submitLogin } from '../../../modules/actions/cognito'
import { cognitoLoadingSelector, isAuthorizedSelector, loginErrorSelector } from '../../../modules/selectors/cognito'

import { ROUTES } from '../../../constants/routes'
import { EMAIL, PASSWORD, initialValues, validationSchema } from './fields'

import { createJsonFromQueryString } from '../../../helpers/url'
import { showToasts } from '../../../helpers/toasts'
import { TOAST_TYPE } from '../../../constants/other'

import useAuthStyles from '../../../styles/common/authPages'
import useDrawerFormStyles from '../../../styles/common/drawerForms'

function Login() {
  const authClasses = useAuthStyles()
  const drawerFormClasses = useDrawerFormStyles()

  const appName = getAppDomainName()

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { search } = useLocation()

  const isAuthorized = useSelector(isAuthorizedSelector)
  const successSubmit = !!isAuthorized

  const { error_code: errorCode } = createJsonFromQueryString(search)

  const onSubmit = useCallback(
    values => {
      dispatch(submitLogin(values))
    },
    [dispatch]
  )

  useEffect(() => {
    if (errorCode === 'invalid_token') {
      showToasts({
        type: TOAST_TYPE.info,
        message: t('You have already created a password, please try and log in.')
      })
    }
  }, [errorCode, t])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })

  return (
    <UnauthorizedPage helmetTitle="Login" className={authClasses.authPage}>
      <div className={authClasses.formContainer}>
        <Form
          formik={formik}
          method="post"
          formName="loginForm"
          successSubmit={!!successSubmit}
          errorSelector={loginErrorSelector}
          isLoadingSelector={cognitoLoadingSelector}
          submitText={t('Log In')}
        >
          <div className={drawerFormClasses.section}>
            <h3 className={drawerFormClasses.sectionTitle}>👋 Welcome back, let’s get you logged in.</h3>
            <p>You're logging in to {appName}. Please enter your email and password below.</p>
            <div className={authClasses.fieldsContainer}>
              <FieldLabelWrapper label={'Your work email'} labelFor={EMAIL}>
                <Field formik={formik} id={EMAIL} name={EMAIL} placeholder={t('Your work email')} />
              </FieldLabelWrapper>
              <FieldLabelWrapper label={'Password'} labelFor={PASSWORD}>
                <Field formik={formik} type="password" id={PASSWORD} name={PASSWORD} placeholder={t('Password')} />
              </FieldLabelWrapper>
            </div>

            <p className={authClasses.description}>
              Passwords contain at least 8 characters, one uppercase, one lowercase, one number and one special
              character
            </p>
          </div>
        </Form>
        <h3 className={classnames(authClasses.authPageSubtitle, authClasses.authPageFooter)}>
          {t('Forgot your password?')} <InternalLink to={ROUTES.resetPassword}>{t('Reset your password')}</InternalLink>
        </h3>
      </div>
    </UnauthorizedPage>
  )
}

export default Login
